import React, {useEffect, useState} from "react";
import {useLocation} from "react-router-dom";
import {translate} from "../../types/EnumTranslations";
import RoomImage from "./RoomImage";
import ClickableImage from "../../Components/ClickableImage";
import {Helmet} from "react-helmet-async";
import InfoPanel from "../../Components/InfoPanel";
import {useBuildingKey} from "../../Helpers/utils";
import {BaseComponentImpl, RoomImpl} from "../../types/implementations";
import {useBuilding} from "../../Helpers/BuildingContext";
import {LevelModel} from "../../types/protocols";
import {BreadcrumbComponent} from "../../Components/BreadCrumb";
import {BaseType, BuildComponentType, RoomType} from "../../types/Enums";
import ComponentSection from "../../Components/ComponentSection";

const RoomPage: React.FC = () => {
    const buildingKey = useBuildingKey();
    const [room, setRoom] = useState<RoomImpl | null>(null);
    const location = useLocation();
    const pathSegments = location.pathname.split("/");
    const roomKey = pathSegments[pathSegments.length - 1];
    const [level, setLevel] = useState<LevelModel|undefined>(undefined);

    const { currentBuilding, error, loading, fetchBuilding } = useBuilding();

    useEffect(() => {
        const loadBuildingData = async () => {
            if (buildingKey) {
                await fetchBuilding(buildingKey);
            }
        };

        loadBuildingData().then();
    }, [buildingKey, fetchBuilding]);

    useEffect(() => {
        if (currentBuilding?.model.structure?.rooms && !loading) {
            const roomData = currentBuilding.model.structure.rooms[roomKey];
            if (roomData) {
                const newRoom = new RoomImpl(roomData);
                setRoom(newRoom);
                setLevel(currentBuilding.model.structure?.levels?.[roomData.levelKey || ""]);
            }
        }
    }, [currentBuilding, roomKey, loading]);

    if (loading) return <>Hämtar data ...</>;
    if (error) return <p>{error}</p>;
    if (!currentBuilding) return <p>No building data available</p>;
    if (!room) return <p>No room data available</p>;
    if (!buildingKey) return <>Building key missing</>;

    const ceilings = currentBuilding.model.structure?.components
        ? Object.values(currentBuilding.model.structure?.components)
            .filter((component) => component.type === BaseType.Ceiling && component.roomKey === roomKey)
            .map((component => {
            return new BaseComponentImpl(component)
        }))
        : [];

    const walls = currentBuilding.model.structure?.components
        ? Object.values(currentBuilding.model.structure?.components)
            .filter((component) => component.type === BaseType.Wall && component.roomKey === roomKey)
            .map((component => {
                return new BaseComponentImpl(component)
            }))
        : [];

    const floors = currentBuilding.model.structure?.components
        ? Object.values(currentBuilding.model.structure?.components)
            .filter((component) => component.type === BaseType.Floor && component.roomKey === roomKey)
            .map((component => {
                return new BaseComponentImpl(component)
            }))
        : [];

    const utilities = currentBuilding.model.structure?.components
        ? Object.values(currentBuilding.model.structure?.components)
            .filter((component) => component.type === BaseType.Utility && component.parentRoomKeys?.value?.includes(roomKey))
            .map((component => {
                return new BaseComponentImpl(component)
            }))
        : [];

    //const riskConstructions = riskConstructionsToItemList(findRiskConstructionsForRoom(room))
    const breadCrumbItems = level ?
        [
            {text: currentBuilding.summary.name ?? 'Byggnad', link: `/building/${buildingKey}`},
            {text: `Våning ${level.number}`, link: `/building/${buildingKey}/level/${level.key}`},
            {text: room.displayName()}
        ]
        : [
            {text: currentBuilding.summary.name ?? 'Byggnad', link: `/building/${buildingKey}`},
            {text: room.displayName()}
        ];

    return (
        <>
            {
                <BreadcrumbComponent
                    items={breadCrumbItems}
                />
            }

            <div style={{ padding: "20px", maxWidth: '1500px'}}>
                <Helmet>
                    <title>{room.displayName()}</title>
                </Helmet>

                <h3 style={{marginBottom: '20px'}}>
                    {room.displayName()}
                </h3>
                <div
                    style={{
                        width: '100%',
                        backgroundColor: 'white',
                        borderRadius: '10px',
                        padding: '40px',
                        marginBottom: '20px',
                        display: 'flex',
                        gap: '40px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        flexDirection: 'column',
                    }}
                >
                    <div style={{display: 'flex', gap: '40px' }}>
                        <div style={{ flex: 1, height: 'fit-content' }}>
                            <h4 style={{marginBottom: '20px'}}>Placering</h4>
                            {room?.location &&
                                <div style={{
                                    boxShadow: '0 5px 15px rgba(0,0,0,0.15)',
                                    borderRadius: '10px',
                                    overflow: 'hidden',
                                }}>
                                    <RoomImage imageUrl={level?.planImage?.fullSizeUrl || ''} location={room.location}/>
                                </div>
                            }
                        </div>

                        <div style={{ flex: 1, }}>
                            <h4 style={{marginBottom: '20px'}}>Rumsfoton</h4>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px'}}>
                                {
                                    room?.photos ?
                                        Object.entries(room.photos).map(([key, photo]) => {
                                            return (
                                                <div key={key} >
                                                    <ClickableImage buildingKey={buildingKey} photo={photo}/>
                                                </div>
                                            )
                                        })
                                        :
                                        <>Inga foton</>
                                }
                            </div>
                        </div>

                        <div style={{ flex: 1, marginRight: '80px', maxWidth: '350px' }}>
                            <h4 style={{marginBottom: '20px'}}>Fakta</h4>
                            <InfoPanel
                                items={[
                                    {label: 'Våning', value: level?.number ?? "-"},
                                    {label: 'Rumsnummer', value: room.number ?? "-"},
                                    {label: 'Rumstyp', value: translate(room.type)},
                                    {label: 'Eget namn', value: room.label ?? "-"},
                                    ]}/>

                            {
                                /*
                                riskConstructions.length != 0 &&
                                <WarningViewComponent title={"Riskkonstruktioner"} color={WarningColor.WarningOrange} hazards={riskConstructions}/>
                                */
                            }

                        </div>
                    </div>

                    <div style={{maxWidth: '66%', display: 'flex', flexDirection: 'column', gap: '40px'}}>

                        {
                            utilities.length !== 0 &&
                            <ComponentSection
                                title={"Fastighetsteknik"}
                                components={utilities}
                                buildingKey={buildingKey}
                                icon={`rooms/${RoomType.RoomUtility}`}
                            />
                        }

                        {ceilings.length !== 0 &&
                            <ComponentSection
                                title={"Innertak"}
                                components={ceilings}
                                buildingKey={buildingKey}
                                icon={`types/${BuildComponentType.InteriorCeiling}`}
                            />
                        }

                        {
                            walls.length !== 0 &&
                            <ComponentSection
                                title={"Innerväggar"}
                                components={walls}
                                buildingKey={buildingKey}
                                icon={`types/${BuildComponentType.InteriorWall}`}
                            />
                        }

                        {
                            floors.length !== 0 &&
                            <ComponentSection
                                title={"Golv"}
                                components={floors}
                                buildingKey={buildingKey}
                                icon={`types/${BuildComponentType.InteriorFloor}`}
                            />
                        }
                    </div>
                </div>
            </div>
        </>
    );
};

export default RoomPage;
